import React, { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, ScrollRestoration, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import theme from './theme/theme';
import CatalogRouteGuard from './components/CatalogRouteGuard';
import ProtectedRoute from './components/ProtectedRoute';
import BusinessLayout from './components/business/BusinessLayout';

// ── Public pages ──
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const Login = React.lazy(() => import('./pages/Login'));
const Signup = React.lazy(() => import('./pages/Signup'));

// ── Customer pages ──
const Home = React.lazy(() => import('./pages/Home'));
const CatalogView = React.lazy(() => import('./pages/CatalogView'));
const ItemDetail = React.lazy(() => import('./pages/ItemDetail'));
const BusinessDetails = React.lazy(() => import('./pages/BusinessDetails'));
const Cart = React.lazy(() => import('./pages/Cart'));
const Checkout = React.lazy(() => import('./pages/Checkout'));
const OrderConfirmation = React.lazy(() => import('./pages/OrderConfirmation'));
const ARView = React.lazy(() => import('./pages/ARView'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

// ── Business pages ──
const BusinessCatalogs = React.lazy(() => import('./pages/business/BusinessCatalogs'));
const BusinessCatalogDetail = React.lazy(() => import('./pages/business/BusinessCatalogDetail'));
const BusinessItemDetail = React.lazy(() => import('./pages/business/BusinessItemDetail'));
const BusinessItemEdit = React.lazy(() => import('./pages/business/BusinessItemEdit'));
const BusinessProfile = React.lazy(() => import('./pages/business/BusinessProfile'));
const BusinessAnalytics = React.lazy(() => import('./pages/business/BusinessAnalytics'));
const QRScanHandler = React.lazy(() => import('./components/QRScanHandler'));

// Loading fallback for lazy-loaded chunks
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

// Root layout component with CartProvider
function RootLayout() {
  return (
    <>
      <ScrollRestoration
        getKey={(location) => {
          // Use pathname + search as key to ensure proper restoration
          return location.pathname + location.search;
        }}
      />
      <CartProvider>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </CartProvider>
    </>
  );
}

// Create router with proper scroll restoration
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // ── Public pages ──
      {
        index: true,
        element: <LandingPage />,
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'signup',
        element: <Signup />,
      },

      // ── Business Dashboard (protected) ──
      {
        path: 'business',
        element: <ProtectedRoute />,
        children: [
          {
            element: <BusinessLayout />,
            children: [
              { index: true, element: <Navigate to="catalogs" replace /> },
              { path: 'catalogs', element: <BusinessCatalogs /> },
              { path: 'catalogs/:catalogId', element: <BusinessCatalogDetail /> },
              { path: 'catalogs/:catalogId/items/new', element: <BusinessItemEdit /> },
              { path: 'catalogs/:catalogId/items/:itemId', element: <BusinessItemDetail /> },
              { path: 'catalogs/:catalogId/items/:itemId/edit', element: <BusinessItemEdit /> },
              { path: 'profile', element: <BusinessProfile /> },
              { path: 'analytics', element: <BusinessAnalytics /> },
              // Future placeholder: orders
              { path: 'orders', element: <Navigate to="/business/catalogs" replace /> },
            ],
          },
        ],
      },

      // ── Customer menu entry (legacy Home) ──
      {
        path: 'menu',
        element: <Home />,
      },

      // ── QR Scan Handler ──
      {
        path: 'qrscan/*',
        element: <QRScanHandler />,
      },

      // ── Customer catalog routes (QR code entry) ──
      {
        path: 'catalog/:catalogId',
        element: <CatalogRouteGuard />,
        children: [
          // Browse-only routes (without tableId) - ordering disabled
          {
            index: true,
            element: <CatalogView />,
          },
          {
            path: 'item/:itemId',
            element: <ItemDetail />,
          },
          {
            path: 'item/:itemId/ar-view',
            element: <ARView />,
          },
          {
            path: 'item/:itemId/variation/:variationId/ar-view',
            element: <ARView />,
          },
          {
            path: 'business-details',
            element: <BusinessDetails />,
          },
          // Table-based routes (primary - accessed via QR codes)
          {
            path: 'table/:tableId',
            element: <CatalogView />,
          },
          {
            path: 'table/:tableId/item/:itemId',
            element: <ItemDetail />,
          },
          {
            path: 'table/:tableId/business-details',
            element: <BusinessDetails />,
          },
          {
            path: 'table/:tableId/cart',
            element: <Cart />,
          },
          {
            path: 'table/:tableId/checkout',
            element: <Checkout />,
          },
          {
            path: 'table/:tableId/order-confirmation/:orderId',
            element: <OrderConfirmation />,
          },
        ],
      },
      // Catch-all route for 404 errors
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
